import React, { useState, useContext, useEffect } from "react";
import Basket from "../Basket";
import { AuthContext } from "../../AuthContext";
import { useBasket } from "../../basketContext";
import {
  ShoppingBagIcon,
  UserCircleIcon,
  LockClosedIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import Button from "../Button";
import { useNavigate, useLocation } from "react-router-dom";
import Divider from "../Divider/Divider";
import ProgressBar from "../ProgressBar/ProgressBar";
import { SparkleIcon } from "../SparkleIcon";
import { useIsAdmin } from "../../hooks/useIsAdmin";

const navigation = [{ name: "Shop Prints", href: "/prints", current: false }];

const Header = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userCreateCredits, setUserCreateCredits] = useState(0);
  const { signOut, session, createCredits } = useContext(AuthContext);
  const { basketQuantity, isBasketOpen, toggleBasket } = useBasket();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckout = location.pathname === "/checkout";
  // The create-print chat has its own composer, so the floating shortcut to it
  // would only sit on top of the conversation.
  const isCreatePrint = location.pathname === "/generate-print";

  const toggleUserMenu = () => {
    if (session && session.user.id) {
      setIsUserMenuOpen(!isUserMenuOpen);
    } else {
      navigate("/login");
    }
  };

  const handleSignOut = async () => {
    toggleUserMenu();
    await signOut();
    navigate("/login");
  };

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  useEffect(() => setUserCreateCredits(createCredits), [createCredits]);

  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isBasketOpen);
    return () => document.body.classList.remove("overflow-hidden");
  }, [isBasketOpen]);

  return (
    <>
    <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
      <div className="flex md:items-center md:justify-center sm:items-stretch sm:justify-start ">
        <a
          href="/"
          className="flex flex-shrink-0 items-center gap-2 text-xl font-semibold"
        >
          <SparkleIcon className="h-7 w-7 text-slate-950 sm:h-9 sm:w-9" />
          <span className="whitespace-nowrap font-serif text-lg font-bold text-slate-950 sm:text-2xl">
            Create Prints
          </span>
        </a>
        {!isCheckout && (
        <div className="ml-3 sm:ml-12 md:block">
          <div className="flex space-x-6">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={classNames(
                  item.current
                    ? "bg-slate-950 text-white"
                    : "text-slate-750 hover:underline-offset-8 hover:underline hover:slate-750",
                  "transition-all ease-in-out whitespace-nowrap rounded-md px-2 py-2 font-medium sm:px-3"
                )}
                aria-current={item.current ? "page" : undefined}
              >
                {item.name}
              </a>
            ))}
            {/* Admins only; on phones it lives in the account menu instead. */}
            {isAdmin && (
              <a
                href="/admin"
                className="hidden whitespace-nowrap rounded-md px-2 py-2 font-medium text-slate-750 transition-all ease-in-out hover:underline hover:underline-offset-8 sm:inline-block sm:px-3"
              >
                Admin
              </a>
            )}
          </div>
        </div>
        )}
      </div>
      {isCheckout ? (
        <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
          <LockClosedIcon className="h-4 w-4" />
          Secure checkout
        </div>
      ) : (
      <div className="flex items-center justify-self-end">
        <div className="flex">
          <div
            className="text-slate-900 flex flex-row cursor-pointer"
            onClick={toggleBasket}
          >
            {basketQuantity > 0 && <div>{basketQuantity}</div>}
            <ShoppingBagIcon className="h-6 w-6 mx-2" />
          </div>
          <div className="relative">
            {session?.user.identities[0].identity_data.picture ? (
              <img
                onClick={toggleUserMenu}
                className="mx-2 w-6 rounded-full cursor-pointer"
                src={session?.user.identities[0].identity_data.avatar_url}
                alt="Your Account"
              />
            ) : (
              <UserCircleIcon
                className="h-6 w-6 mx-2 cursor-pointer"
                onClick={toggleUserMenu}
              />
            )}
            <div
              aria-hidden={!isUserMenuOpen}
              className={`absolute right-0 top-8 z-50 rounded-md bg-white p-2 text-sm text-slate-900 shadow-lg transition duration-100 ease-in-out ${
                isUserMenuOpen
                  ? "visible translate-y-0 opacity-100"
                  : "pointer-events-none invisible -translate-y-2 opacity-0"
              }`}
            >
                  <div className="w-56 p-2">
                    <div className="text-lg">
                      {session?.user.identities[0].identity_data.full_name}
                    </div>
                    <div className="mt-2 mb-4">
                      <ProgressBar
                        current={userCreateCredits}
                        label="Remaining Create Credits"
                        total={5}
                        className="my-2 bg-stone-300"
                      />
                      <p className="text-slate-400 text-xs">
                        Create credits are restored after a purchase or 30 days.
                      </p>
                    </div>
                    <Divider />
                  </div>

                  <ul>
                    <li className="py-2 px-2.5 hover:bg-slate-100 rounded-sm">
                      <a href="/account" className="block">Account</a>
                    </li>
                    {isAdmin && (
                      <li className="py-2 px-2.5 hover:bg-slate-100 rounded-sm">
                        <a href="/admin" className="block">Admin</a>
                      </li>
                    )}
                    <li className="py-2 px-2.5 hover:bg-slate-100 rounded-sm">
                      <button onClick={handleSignOut} className="w-full text-left">Sign out</button>
                    </li>
                  </ul>
            </div>
          </div>
        </div>
        {!isCreatePrint && (
        <Button
          className="hidden whitespace-nowrap md:ml-6 md:inline-flex"
          url={session?.user.id ? `/generate-print` : `/login`}
        >
          Create Your Art Print
        </Button>
        )}

        <div
          aria-hidden={!isBasketOpen}
          inert={isBasketOpen ? undefined : ""}
          className={`fixed inset-0 z-40 overflow-hidden ${
            isBasketOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
        >
          <aside
            className={`absolute bottom-0 right-0 top-0 z-50 w-full bg-white p-4 shadow-md transition-transform duration-200 ease-in-out sm:min-w-[400px] sm:w-1/4 ${
              isBasketOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <Basket handleClose={toggleBasket} />
          </aside>
          <button
            type="button"
            aria-label="Close basket"
            tabIndex={isBasketOpen ? 0 : -1}
            className={`absolute inset-0 z-40 bg-slate-950/60 transition-opacity duration-200 ${
              isBasketOpen
                ? "visible opacity-100"
                : "pointer-events-none invisible opacity-0"
            }`}
            onClick={toggleBasket}
          />
        </div>
      </div>
      )}
    </header>
    {!isCheckout && !isCreatePrint && (
    <Button
      size="icon"
      url="/generate-print"
      aria-label="Create your art print"
      className="fixed bottom-6 right-6 z-10 drop-shadow-xl md:hidden"
    >
      <PlusIcon className="h-5 w-5" />
    </Button>
    )}
  </>
  );
};

export default Header;
